import Card from "@components/Card";
import CostOriginSplitCard from "./CostOriginSplitCard";
import StockSummaryCard from "./StockSummaryCard";
import { useAuthContext } from "@contexts/auth";
import { useProjectContext } from "@contexts/projects";
import { cardTexts } from "@utils/cardTexts";
import { checkIfCubBased, checkLabel, formatNumberToLocale, formatToPercentage } from "@utils/formatNumber";
import {
  combineMetricEnvelopes,
  deriveMetricEnvelope,
  metricPresentation,
  metricReasonStyle,
} from "@utils/metricPresentation";
import { Fragment } from "react";

// Tendência do total pela composição de carteira (`trend_settings.total_formula =
// "commitment_composition"`, hoje só a CORPAL): o back-end soma as parcelas gravadas pelo
// data-pipeline em `trend_compositions` aos estouros/economias vigentes. Quando a composição
// não existe, a Tendência vem indisponível com um motivo tipado em `metadata.reason`, que o
// card mostra no lugar do valor em vez do "—" genérico.
export const COMMITMENT_COMPOSITION = "commitment_composition";

export const TREND_COMPOSITION_UNAVAILABLE_REASONS = Object.freeze({
  composition_missing: "Composição ainda não calculada",
  missing_registration_fields: "Cadastro da obra sem CNPJ e/ou código reduzido do projeto",
});

// "dd/mm/aaaa hh:mm" no fuso de Brasília -- o instante do cálculo vem em ISO (UTC).
export const formatCompositionComputedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date).map(({ type, value: part }) => [type, part]),
  );
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
};

const FinancialInfo = () => {
  const { activeProject, cubBased } = useProjectContext()
  const { lastMeasure, extraValues } = activeProject
  const { user: { configurations: { dashboard: { cards: { lower } } } } } = useAuthContext();

  const [lastMeasureYear, lastMeasureMonth] = lastMeasure?.date?.split('-') ?? [];
  const lastDayOfMonth = lastMeasureYear && lastMeasureMonth
    ? new Date(Number(lastMeasureYear), Number(lastMeasureMonth), 0).getDate()
    : null;
  const transformedDate = lastDayOfMonth
    ? `${lastDayOfMonth}/${lastMeasureMonth.padStart(2, '0')}`
    : null;
  const finiteScheduleValue = (value) => (
    value !== null && value !== undefined && Number.isFinite(Number(value))
      ? Number(value)
      : null
  );
  const optionalPercentage = (value) => {
    const parsed = finiteScheduleValue(value);
    return parsed === null ? "—" : formatToPercentage(parsed);
  };
  const measuredAccumulated = finiteScheduleValue(lastMeasure?.measured_accumulated_percentage);
  const baseAccumulated = finiteScheduleValue(lastMeasure?.base_accumulated_percentage);
  const predictedAccumulated = finiteScheduleValue(lastMeasure?.baseline_accumulated_percentage);
  const baseDiffValue = measuredAccumulated === null || baseAccumulated === null
    ? null
    : measuredAccumulated - baseAccumulated;
  const predictedDiffValue = measuredAccumulated === null || predictedAccumulated === null
    ? null
    : measuredAccumulated - predictedAccumulated;
  const baseDiff = optionalPercentage(baseDiffValue);
  const predDiff = optionalPercentage(predictedDiffValue);

  const unavailableMetric = (id, unit = "currency") => ({
    id,
    value: null,
    unit,
    status: "source_unavailable",
  });
  const trendEnvelope = activeProject.metrics?.trend ?? unavailableMetric("trend");
  const budgetEnvelope = activeProject.metrics?.budget ?? unavailableMetric("budget");
  const committedEnvelope = activeProject.metrics?.committed ?? unavailableMetric("committed");
  const budgetIndexedEnvelope = cubBased
    ? budgetEnvelope
    : activeProject.metrics?.budget_indexed ?? unavailableMetric("budget_indexed");
  const availableBudgetEnvelope = activeProject.metrics?.available_budget
    ?? unavailableMetric("available_budget");
  const availableToContractEnvelope = activeProject.metrics?.available_to_contract
    ?? unavailableMetric("available_to_contract");
  const indexerEnvelope = (id, value) => (
    typeof value === "number" && Number.isFinite(value)
      ? { id, value, unit: "index", status: "ok" }
      : { id, value: null, unit: "index", status: "source_unavailable" }
  );
  const originalIndexerEnvelope = indexerEnvelope(
    "original_indexer", activeProject?.originalIndexer?.value,
  );
  const currentIndexerEnvelope = indexerEnvelope(
    "current_indexer", activeProject?.indexer?.value,
  );
  const areaEnvelope = Number.isFinite(activeProject.area) && activeProject.area !== 0
    ? { id: "area", value: activeProject.area, unit: "m2", status: "ok" }
    : { id: "area", value: null, unit: "m2", status: "no_basis" };
  const presentMetric = (envelope, slot, formatter = formatNumberToLocale) => metricPresentation(
    envelope,
    {
      formatter,
      reasonId: `metric-financial-${slot}-reason`,
    },
  );
  const asDisplayedMoney = (envelope) => cubBased
    ? deriveMetricEnvelope(
      envelope,
      (value) => {
        const indexer = activeProject?.originalIndexer?.value;
        return typeof indexer === "number" && Number.isFinite(indexer) && indexer !== 0
          ? value / indexer
          : null;
      },
      { id: `${envelope?.id || "metric"}_cub`, unit: "CUB" },
    )
    : envelope;
  const presentMoney = (envelope, slot) => presentMetric(asDisplayedMoney(envelope), slot);
  const trendPresentation = presentMoney(trendEnvelope, "trend");
  const trendMetadata = trendEnvelope?.metadata ?? {};
  const trendFromComposition = trendEnvelope?.source === COMMITMENT_COMPOSITION
    || trendMetadata.total_formula === COMMITMENT_COMPOSITION;
  const trendUnavailableReason = !trendPresentation.available && trendFromComposition
    ? TREND_COMPOSITION_UNAVAILABLE_REASONS[trendMetadata.reason] ?? null
    : null;
  const composition = trendPresentation.available && trendFromComposition
    ? trendMetadata.composition ?? null
    : null;
  const compositionMoney = (value, slot) => presentMoney(
    { id: `trend_${slot}`, value, unit: "currency", status: "ok" },
    `trend-${slot}`,
  ).text;
  const budgetPresentation = presentMoney(budgetEnvelope, "budget");
  const budgetIndexedPresentation = presentMoney(budgetIndexedEnvelope, "budget-indexed");
  const committedPresentation = presentMoney(committedEnvelope, "committed");
  const availableIndexedEnvelope = combineMetricEnvelopes(
    [budgetIndexedEnvelope, committedEnvelope],
    ([indexedBudget, committed]) => indexedBudget - committed,
    { id: "available_indexed", unit: "currency" },
  );
  const availableIndexedPresentation = presentMoney(
    availableIndexedEnvelope,
    "available-indexed",
  );
  const trendDeviationEnvelope = combineMetricEnvelopes(
    [budgetEnvelope, trendEnvelope],
    ([budgetValue, trendValue]) => budgetValue - trendValue,
    { id: "trend_deviation", unit: "currency" },
  );
  const trendPerAreaEnvelope = combineMetricEnvelopes(
    [trendEnvelope, areaEnvelope],
    ([trendValue, area]) => trendValue / area,
    { id: "trend_per_area", unit: "currency_per_area" },
  );
  const budgetPerAreaEnvelope = combineMetricEnvelopes(
    [budgetEnvelope, areaEnvelope],
    ([budgetValue, area]) => budgetValue / area,
    { id: "budget_per_area", unit: "currency_per_area" },
  );
  const budgetIndexedPerAreaEnvelope = combineMetricEnvelopes(
    [budgetIndexedEnvelope, areaEnvelope],
    ([budgetValue, area]) => budgetValue / area,
    { id: "budget_indexed_per_area", unit: "currency_per_area" },
  );
  const deviationPercentageEnvelope = combineMetricEnvelopes(
    [budgetEnvelope, trendEnvelope],
    ([budgetValue, trendValue]) => budgetValue !== 0
      ? ((budgetValue - trendValue) / budgetValue) * 100
      : null,
    { id: "trend_deviation_percentage", unit: "percentage" },
  );
  const trendDeviationPresentation = presentMoney(trendDeviationEnvelope, "trend-deviation");
  const trendPerAreaPresentation = presentMoney(trendPerAreaEnvelope, "trend-per-area");
  const budgetPerAreaPresentation = presentMoney(budgetPerAreaEnvelope, "budget-per-area");
  const budgetIndexedPerAreaPresentation = presentMoney(
    budgetIndexedPerAreaEnvelope,
    "budget-indexed-per-area",
  );
  const deviationPercentagePresentation = presentMetric(
    deviationPercentageEnvelope,
    "deviation-percentage",
    formatNumberToLocale,
  );
  const availableBudgetPresentation = presentMoney(
    availableBudgetEnvelope,
    "available-budget",
  );
  const availableToContractPresentation = presentMoney(
    availableToContractEnvelope,
    "available-to-contract",
  );
  const cubVariationPresentation = presentMetric(
    combineMetricEnvelopes(
      [originalIndexerEnvelope, currentIndexerEnvelope],
      ([original, current]) => current !== 0 ? (1 - (original / current)) * 100 : null,
      { id: "cub_variation", unit: "percentage" },
    ),
    "cub-variation",
  );
  const metricContent = (presentation) => (
    <>
      <span aria-describedby={presentation.describedBy}>{presentation.text}</span>
      {!presentation.available && (
        <span id={presentation.describedBy} style={metricReasonStyle}>
          {presentation.reason}
        </span>
      )}
    </>
  );

  const trendContent = trendUnavailableReason
    ? (
      <span className="card-value-reason" data-testid="trend-unavailable-reason">
        {trendUnavailableReason}
      </span>
    )
    : metricContent(trendPresentation);
  const computedAt = formatCompositionComputedAt(trendMetadata.computed_at);
  const trendComposition = composition && (
    <span className="card-composition" role="list" aria-label="Composição da Tendência">
      {[
        ["Incorrido (pago + a pagar)", composition.incurred, "incurred"],
        ["Saldo de contratos", composition.contracts_balance, "contracts-balance"],
        ["Saldo de pedidos", composition.orders_balance, "orders-balance"],
        ["A gastar", composition.to_spend_net, "to-spend"],
        ["Estouros / Economias", composition.extra_values, "extra-values"],
      ].map(([label, value, slot]) => (
        <span key={slot} className="card-composition__row" role="listitem">
          <span className="card-composition__label">{label}</span>
          <span className="card-composition__value">{compositionMoney(value, slot)}</span>
        </span>
      ))}
      {computedAt && (
        <span className="card-composition__computed">Calculado em {computedAt}</span>
      )}
    </span>
  );

  const checkCard = (i) => {
    switch (i) {
      case 'budgetValue':
        return <Card label={checkLabel('Orçamento base')} content={metricContent(budgetPresentation)} tooltip={cardTexts.BASE} />
      case 'budgetIndexedGlobal':
        return <Card label={checkLabel('Orçamento indexado')} content={metricContent(budgetIndexedPresentation)} tooltip={cardTexts.BUDGET_INDEXED_GLOBAL} />
      case 'committedTotal':
        return <Card label={checkLabel('Total comprometido')} content={metricContent(committedPresentation)} tooltip={cardTexts.COMMITED_TOTAL} />

      case 'available':
        return <Card label={checkLabel('Verba disponível')} color={availableBudgetPresentation.available && availableBudgetPresentation.value < 0 ? 'red' : ''} content={metricContent(availableBudgetPresentation)} tooltip={cardTexts.AVAILABLE_FUNDS} />
      case 'availableIndexedGlobal':
        return <Card label={checkLabel('Verba disponível - Indexado')} color={availableIndexedPresentation.available && availableIndexedPresentation.value < 0 ? 'red' : ''} content={metricContent(availableIndexedPresentation)} tooltip={cardTexts.AVAILABLE_FUNDS_INDEXED_GLOBAL} />


      case 'predictedAccumulated':
        return <Card label={`Previsto Acumulado (%)${transformedDate ? ` - ${transformedDate}` : ''}`} content={optionalPercentage(predictedAccumulated)} tooltip={cardTexts.ACCUMULATED_BASE} />
      case 'baseAccumulated':
        return <Card label="Linha de Base Acumulada (%)" content={optionalPercentage(baseAccumulated)} tooltip={cardTexts.ACCUMULATED_BASE_BRASIN} />
      case 'aggregatedAccumulated':
        return <Card label='Medido acumulado (%)' content={optionalPercentage(measuredAccumulated)} tooltip={cardTexts.ACCUMULATED_AGGREGATED} />
      case 'difBaseAggr':
        return <Card label="Linha de Base X Medido (%)" content={baseDiff} color={baseDiffValue === null ? '' : baseDiffValue > 0 ? 'green' : 'red'} tooltip={cardTexts.BASE_AGGREGATED_DIF_BRASIN} />
      case 'difPrevAggr':
        return <Card label="Previsto X Medido (%)" content={predDiff} color={predictedDiffValue === null ? '' : predictedDiffValue > 0 ? 'green' : 'red'} tooltip={cardTexts.BASE_AGGREGATED_DIF} />
      case 'trend':
        return <Card label={checkLabel('Tendência')} content={trendContent} subcontent={trendComposition || undefined} color={trendPresentation.available && budgetIndexedPresentation.available && trendPresentation.value >= budgetIndexedPresentation.value ? 'red' : ''} tooltip={cardTexts.TREND} />

      case 'budgetArea':
        return <Card label='Orçamento por M²' content={metricContent(budgetPerAreaPresentation)} />
      case 'budgetIndexedArea':
        return <Card label='Orçamento Indexado por M²' content={metricContent(budgetIndexedPerAreaPresentation)} />


      case 'trendDeviation':
        return <Card label={checkLabel('Desvio de Tendência')} color={trendDeviationPresentation.available && trendDeviationPresentation.value < 0 ? 'red' : ''} content={metricContent(trendDeviationPresentation)} />
      case 'trendPerArea':
        return <Card label={checkLabel('Tendência/m²')} content={metricContent(trendPerAreaPresentation)} />
      // ⚠️ O `.toFixed(2)` aqui era o bug: ele devolve STRING, e
      // `formatNumberToLocale` só aceita `Number.isFinite(value)` — string cai no
      // fallback e o card exibia "0,00" para qualquer desvio. Medido em GESSELE012
      // (orçamento R$ 107.876.392,32, tendência R$ 137.990.782,83): o valor é
      // −27,92% e a tela mostrava 0,00. O arredondamento já é do formatter.
      case 'deviationPercentage': {
        return <Card label='Desvio de Tendência (%)' color={deviationPercentagePresentation.available && deviationPercentagePresentation.value < 0 ? 'red' : ''} content={metricContent(deviationPercentagePresentation)} />
      }

      case 'cubVariation':
        return <Card label='Variação de CUB (%)' content={metricContent(cubVariationPresentation)} />

      case 'extraValues':
        return <Card label='Estouros / Economias' content={formatNumberToLocale(checkIfCubBased(extraValues?.total))} />

      case 'toContract':
        return <Card label={checkLabel('Valores a contratar')} content={metricContent(availableToContractPresentation)} />

      default:
        break;
    }
  }

  return (
    <>
      <div className="grid gap-24 columns-2-tablet columns-4-laptop-l margin-top-40 margin-bottom-40">
        {lower.filter(i => i !== 'costOriginSplit').map(i => {
          return <Fragment key={i}>{checkCard(i)}</Fragment>
        })}
      </div>
      {lower?.includes('costOriginSplit') && <CostOriginSplitCard />}
      {/* Sem gate de configuração de propósito: o card só renderiza quando a obra TEM
          estoque, e o que ele diz é diagnóstico (quanto está sendo ignorado, descartado ou
          obsoleto) — esconder atrás de um flag por usuário manteria o problema invisível,
          que é justamente o que ele existe para resolver. */}
      <StockSummaryCard />
    </>
  )
};

export default FinancialInfo;
